import { useCallback, useEffect, useState } from 'react'
import { useGame } from './hooks/useGame.ts'
import { useAudio } from './hooks/useAudio.ts'
import { useUndo } from './hooks/useUndo.ts'
import { TitleScreen } from './components/TitleScreen.tsx'
import { DifficultyScreen } from './components/DifficultyScreen.tsx'
import { GameScreen } from './components/GameScreen.tsx'
import { IntroOverlay } from './components/IntroOverlay.tsx'
import { FinaleOverlay } from './components/FinaleOverlay.tsx'
import { ResultScreen } from './components/ResultScreen.tsx'
import { RankingScreen } from './components/RankingScreen.tsx'
import { MultiplayerMenu } from './components/MultiplayerMenu.tsx'
import { MatchmakingModal } from './components/MatchmakingModal.tsx'
import { FriendMatchModal } from './components/FriendMatchModal.tsx'
import { MultiplayerLobby } from './components/MultiplayerLobby.tsx'
import { MultiplayerGameScreen } from './components/MultiplayerGameScreen.tsx'
import { InviteFriendsModal } from './components/InviteFriendsModal.tsx'
import { AuthLoginModal } from './components/AuthLoginModal.tsx'
import { AdRewardedOverlay } from './components/AdRewardedOverlay.tsx'
import { AdNekoPopup } from './components/AdNekoPopup.tsx'
import { AdBanner } from './components/AdBanner.tsx'
import { OfflineBanner } from './components/OfflineBanner.tsx'
import { AUTH_UI } from './config/auth.ts'
import { getYmgSdk, probeYmgSdk } from './services/sdk.ts'
import { clearProfileCache, syncHubProfile, type LocalProfile } from './services/hubProfile.ts'
import { isCapacitorNative } from './utils/platform.ts'
import {
  initializeSocialLoginProviders,
  restoreAuthHint,
  subscribeAuthState,
} from './services/auth.ts'
import { processPendingInvite } from './services/social.ts'
import { ApiError, NetworkError, onReauthRequired } from './services/ymgAuth.ts'
import { startHubProbe, onHubRecovery } from './services/hubProbe.ts'
import { invalidateLeaderboardCache } from './services/leaderboard.ts'
import { drainQueuedScores } from './services/scores.ts'
import { hideBannerAd, preloadRewardedAd, showBannerAd } from './services/ads.ts'
import { trackSdkOutcome } from './services/analytics.ts'
import splashImage from './assets/splashscreen_portrait.webp'
import bg2Image from './assets/bg2.webp'
import block1Image from './assets/block1.webp'
import block2Image from './assets/block2.webp'
import titleLogoImage from './assets/title_logo.webp'
import type { Difficulty } from './config/ai-difficulty.ts'
import type { RoomInfo } from './config/multiplayer.ts'
import type { BattleResult } from './config/realtime.ts'
import type { AdScreen } from './config/ads.ts'
import { t } from './i18n/translations.ts'

const YMG_API_BASE_URL = (import.meta.env.VITE_YMG_API_BASE_URL || '').trim()

export function App() {
  const {
    state,
    blackScore,
    whiteScore,
    highscore,
    isNewHighscore,
    goToDifficulty,
    goToRanking,
    startGame,
    retry,
    returnToTitle,
    handleCellClick,
    handlePopupComplete,
    hasSnapshot,
    isAiThinking,
    undoLastPair,
  } = useGame()

  const {
    isUnlocked: undoIsUnlocked,
    remainingUses: undoRemainingUses,
    isUnlocking: undoIsUnlocking,
    unlockViaAd: unlockUndoViaAd,
    consumeUndo,
    reset: resetUndo,
  } = useUndo()

  const { playBgm, resumeBgmIfPaused } = useAudio()
  const [profile, setProfile] = useState<LocalProfile | null>(null)

  // --- 起動スプラッシュ関連 ---
  // isReady が true になるまで <SweeperGame/> 相当のメイン UI を描画しない。
  // error が非 null の場合は更に優先してエラー画面を出す（再読み込み / 再ログイン導線）。
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [requiresReauth, setRequiresReauth] = useState(false)
  const [reauthLoginOpen, setReauthLoginOpen] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [loadingMessage, setLoadingMessage] = useState(t('loadingBoot'))
  const [titleBackRequestId, setTitleBackRequestId] = useState(0)
  const [, setLocaleRevision] = useState(0)

  // --- マルチプレイヤー関連の state ---
  // 'idle'      : NPC フロー（既存）
  // 'menu'      : MultiplayerMenu 表示中
  // 'random'    : ランダムマッチモーダル表示中
  // 'friend'    : フレンドマッチモーダル表示中
  // 'lobby'     : マッチ成立 / 参加済み、MultiplayerLobby 表示中
  // 'battle'    : リアルタイム対戦中（MultiplayerGameScreen 表示中、T15）
  type MultiplayerPhase = 'idle' | 'menu' | 'random' | 'friend' | 'lobby' | 'battle'
  const [multiplayerPhase, setMultiplayerPhase] = useState<MultiplayerPhase>('idle')
  const [lobbyRoom, setLobbyRoom] = useState<RoomInfo | null>(null)

  // T16: フレンド招待モーダル
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLoginOpen, setInviteLoginOpen] = useState(false)

  // YMG SDK 初期化 + プロフィール取得 + 画像プリロード（3フェーズのローディング）
  //
  // Phase 0-40%: LIFF 済なら反映済、YMG SDK 初期化
  // Phase 40-60%: プロフィール同期 + 保留招待処理
  // Phase 60-100%: ゲームで使う webp 画像のプリロード
  //
  // SDK 初期化で throw された場合（LIFF トークン欠落等）は setError() でエラー画面を表示する。
  // 画像プリロードは失敗しても続行（Vite が content-hashed asset を保証するため fail-open で安全）。
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // --- Phase 1: SDK 初期化 (0-40%) ---
        setLoadingMessage(t('loadingConnect'))
        setLoadingProgress(10)
        // SDK 到達の成否だけを個別に計測する（外側の catch は Phase 2-3 の失敗も
        // 拾うため、ここで包まないと profile 同期の失敗が sdk_failed になる）。
        try {
          await getYmgSdk()
          trackSdkOutcome(null)
        } catch (err) {
          trackSdkOutcome(err)
          throw err
        }
        await restoreAuthHint()
        if (cancelled) return
        setLoadingProgress(40)

        // --- Phase 2: プロフィール同期 + 招待処理 (40-60%) ---
        setLoadingMessage(t('loadingProfile'))
        const p = await syncHubProfile()
        if (cancelled) return
        setProfile(p)
        setLoadingProgress(55)

        try {
          await processPendingInvite()
        } catch (err) {
          // 招待受諾失敗はゲームプレイを阻害しない（コード期限切れ等は typical）。
          // ただしサイレントに握り潰すのではなく警告ログを残す。
          console.warn('[App] processPendingInvite failed:', err)
        }
        void drainQueuedScores().then((processed) => {
          if (processed > 0) invalidateLeaderboardCache()
        }).catch((err) => {
          console.warn('[App] drainQueuedScores failed:', err)
        })
        if (cancelled) return
        setLoadingProgress(60)

        void preloadRewardedAd().catch((err) => {
          console.warn('[App] AdMob rewarded preload failed:', err)
        })

        // --- Phase 3: 画像プリロード (60-100%) ---
        setLoadingMessage(t('loadingAssets'))
        await preloadSplashAssets((fraction) => {
          if (!cancelled) setLoadingProgress(60 + Math.round(fraction * 40))
        })
        if (cancelled) return

        setLoadingProgress(100)
        setLoadingMessage(t('loadingReady'))
        await new Promise((resolve) => setTimeout(resolve, 300))
        if (cancelled) return
        setIsReady(true)
      } catch (err) {
        console.warn('[App] Initialization failed:', err)
        if (!cancelled) {
          setRequiresReauth(isReauthError(err))
          setError(toUserFacingErrorMessage(err))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // 認証状態が signed_in に遷移したタイミングで pending invite を再処理する。
  // 初回起動時に URL にパラメータがあったが未ログインだったケースの救済。
  useEffect(() => {
    const unsubscribe = subscribeAuthState((state) => {
      invalidateLeaderboardCache()
      void (async () => {
        if (state.status !== 'signed_in') {
          clearProfileCache()
          setProfile(null)
          return
        }
        try {
          const nextProfile = await syncHubProfile()
          setProfile(nextProfile)
        } catch (err) {
          // post-auth 経路でも userId 未取得のままマルチプレイに進ませないため、
          // 初回ロードと同じく全エラーをエラー画面に倒す。
          // 401（要再認証）は notifyReauthRequired 経由でも発火するが、二重でも
          // setError は冪等（同じ文字列が入るだけ）なので問題ない。
          console.warn('[App] syncHubProfile (post-auth) failed:', err)
          setRequiresReauth(isReauthError(err))
          setError(toUserFacingErrorMessage(err))
        }
        try {
          await processPendingInvite()
        } catch (err) {
          console.warn('[App] processPendingInvite (post-auth) failed:', err)
        }
        void drainQueuedScores().then((processed) => {
          if (processed > 0) invalidateLeaderboardCache()
        }).catch((err) => {
          console.warn('[App] drainQueuedScores (post-auth) failed:', err)
        })
      })()
    })
    return unsubscribe
  }, [])

  // ymgFetch / hubProfile が 401 を観測した際の再認証要求を UI に反映する。
  // `onReauthRequired` はコールバック登録前に発火した 401 もバッファから配信するため、
  // 早期の SDK 初期化失敗もここで必ず拾える。
  useEffect(() => {
    const unsubscribe = onReauthRequired((err) => {
      console.warn('[App] reauth required:', err)
      setRequiresReauth(true)
      setError(toUserFacingErrorMessage(err))
    })
    return unsubscribe
  }, [])

  // Hub 接続障害監視ループ。offline 中はバックオフ付きで probeYmgSdk を周期実行し、
  // online 復帰時に leaderboard キャッシュを破棄して fresh データを得られるようにする。
  // 起動済みフラグは hubProbe 側で管理されており、複数回呼んでも no-op。
  useEffect(() => {
    startHubProbe(probeYmgSdk)
    const unsub = onHubRecovery(() => {
      invalidateLeaderboardCache()
      void drainQueuedScores().then((processed) => {
        if (processed > 0) invalidateLeaderboardCache()
      }).catch((err) => {
        console.warn('[App] drainQueuedScores (recovery) failed:', err)
      })
    })
    return unsub
  }, [])

  // Social Login providers の初期化（Capacitor ネイティブのみ実効、Web では noop）。
  // これがないと iOS で `SocialLogin.login({ provider: 'google'/'apple' })` が
  // 「provider not initialized」で失敗する。
  // 参考: ../luckyanimal-balloon-float/src/App.tsx, ../luckyanimal-sweeper/src/App.tsx
  // - iOS: `iOSClientId` + `iOSServerClientId` (= Web Client ID) を渡す
  // - Android/Web: `webClientId` のみで動く
  // 失敗しても起動は継続する（ログイン UI に到達した時点で再度エラー表示する設計）。
  useEffect(() => {
    void (async () => {
      try {
        await initializeSocialLoginProviders()
      } catch (e) {
        // fallback:ok — ログイン UI を開いた段階で再度ハンドリングするため、起動を止めない。
        console.warn('[App] SocialLogin init failed (non-blocking):', e)
      }
    })()
  }, [])

  // フェーズに応じてBGM切り替え
  useEffect(() => {
    if (state.phase === 'title' || state.phase === 'difficulty' || state.phase === 'ranking') {
      playBgm('home')
    } else if (state.phase === 'intro' || state.phase === 'game' || state.phase === 'finale') {
      playBgm('stage')
    }
    // result は stage BGM を継続
  }, [state.phase, playBgm])

  // AdMob バナーは操作の邪魔にならない画面だけで表示する。
  // 盤面・導入/勝敗演出・マルチ対戦中・マッチング中は必ず隠す。
  const bannerAllowed =
    isReady &&
    !error &&
    multiplayerPhase === 'idle' &&
    (state.phase === 'title' || state.phase === 'ranking' || state.phase === 'result')

  const bannerScreen: AdScreen =
    state.phase === 'ranking' ? 'ranking' :
      state.phase === 'result' ? 'result' :
        'title'

  useEffect(() => {
    const nativeBannerAllowed =
      isReady &&
      !error &&
      multiplayerPhase === 'idle' &&
      (state.phase === 'title' || state.phase === 'ranking' || state.phase === 'result')

    void (nativeBannerAllowed ? showBannerAd('bottom') : hideBannerAd())
  }, [error, isReady, multiplayerPhase, state.phase])

  useEffect(() => {
    return () => {
      void hideBannerAd()
    }
  }, [])

  // Undo 関連ハンドラ（useGame と useUndo の責務を App で合流させる）。
  // - 新しいゲーム開始時は Undo の解放フラグ・残回数を初期化する。
  // - Undo 実行時は useUndo.consumeUndo() → 盤面ロールバックの順。消費できないのに
  //   先にロールバックすると残回数の整合が取れなくなるため順序厳守。
  const handleStart = useCallback(
    (difficulty: Difficulty) => {
      resetUndo()
      startGame(difficulty)
    },
    [resetUndo, startGame],
  )

  const handleRetry = useCallback(() => {
    resetUndo()
    retry()
  }, [resetUndo, retry])

  const handleUndoUnlock = useCallback(() => {
    void unlockUndoViaAd()
  }, [unlockUndoViaAd])

  const handleUndo = useCallback(() => {
    // 防御的: UI が canUndo=false を通さない想定だが、念のためここでもガード。
    if (!undoIsUnlocked) return
    if (undoRemainingUses <= 0) return
    if (!hasSnapshot) return
    if (isAiThinking) return
    if (state.phase !== 'game') return

    const consumed = consumeUndo()
    if (!consumed) return
    undoLastPair()
  }, [
    consumeUndo,
    hasSnapshot,
    isAiThinking,
    state.phase,
    undoIsUnlocked,
    undoLastPair,
    undoRemainingUses,
  ])

  // Undo ボタン活性化判定: ゲーム進行中 & プレイヤー手番 & snapshot あり & AI 非思考中。
  const undoCanUndo =
    state.phase === 'game' && !isAiThinking && hasSnapshot

  // finale 中も盤面を表示し続けて演出を見せる
  const showGame =
    state.phase === 'intro' || state.phase === 'game' || state.phase === 'finale'

  // --- マルチプレイヤー系ハンドラ ---
  // TitleScreen からの導線は現時点で未提供（「オンライン対戦」ボタンは将来復活予定）。
  // ロビー/対戦/マッチングの各モーダルは setMultiplayerPhase 経由で遷移する。
  const handleMatched = useCallback((room: RoomInfo) => {
    setLobbyRoom(room)
    setMultiplayerPhase('lobby')
  }, [])

  const handleLobbyExit = useCallback(() => {
    setLobbyRoom(null)
    setMultiplayerPhase('idle')
  }, [])

  // T15: ホストが「対戦開始」を押したらリアルタイム対戦画面に遷移する。
  // ゲスト側は同一 WebSocket 上で host が送る StartMatch 信号を受けて初期化される。
  const handleBattleStart = useCallback(() => {
    setMultiplayerPhase('battle')
  }, [])

  // T15: リアルタイム対戦終了時（勝敗決定 or 切断 or 退出）に呼ばれる。
  // leaveRoom は MultiplayerGameScreen 側で実行済みなので、ここでは画面遷移のみ。
  const handleBattleExit = useCallback((result: BattleResult | null) => {
    console.log('[App] battle ended:', result)
    setLobbyRoom(null)
    setMultiplayerPhase('idle')
  }, [])

  // ロビー/対戦中の自分の userId。
  // syncHubProfile() 経由で Hub の /profiles/me から取得した userId を使う。
  // 取得失敗時は null → Lobby 側で相手識別不能となり、対戦開始ボタンも disabled。
  const currentUserId: string | null = profile?.userId ?? null

  useEffect(() => {
    if (multiplayerPhase !== 'lobby') return
    if (!lobbyRoom) return
    if (!currentUserId) return
    if (lobbyRoom.hostUserId === currentUserId) return
    setMultiplayerPhase('battle')
  }, [currentUserId, lobbyRoom, multiplayerPhase])

  // T16: フレンド招待モーダルハンドラ
  // タイトル画面からの導線は現時点で未提供（「ともだちを誘う」ボタンは将来復活予定）。
  const handleCloseInvite = useCallback(() => {
    setInviteOpen(false)
  }, [])

  // 未認証ユーザーが招待モーダルから「ログイン」を押したときの導線。
  const handleInviteRequireLogin = useCallback(() => {
    setInviteOpen(false)
    setInviteLoginOpen(true)
  }, [])

  const handleInviteLoggedIn = useCallback(() => {
    setInviteLoginOpen(false)
    // ログイン直後に招待モーダルを再開。pending 処理は subscribeAuthState 経由で自動。
    setInviteOpen(true)
  }, [])

  const exitNativeApp = useCallback(() => {
    void (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core')
        if (!Capacitor.isNativePlatform()) return
        const { App: CapacitorApp } = await import('@capacitor/app')
        await CapacitorApp.exitApp()
      } catch (err) {
        console.warn('[App] exitApp failed:', err)
      }
    })()
  }, [])

  useEffect(() => {
    let subscription: { remove: () => Promise<void> } | null = null
    let cancelled = false

    void (async () => {
      const { Capacitor } = await import('@capacitor/core')
      if (!Capacitor.isNativePlatform()) return
      const { App: CapacitorApp } = await import('@capacitor/app')
      subscription = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (!isReady || error) {
          if (canGoBack) window.history.back()
          return
        }

        if (inviteLoginOpen) {
          setInviteLoginOpen(false)
          return
        }
        if (inviteOpen) {
          setInviteOpen(false)
          return
        }
        if (multiplayerPhase === 'random' || multiplayerPhase === 'friend') {
          setMultiplayerPhase('menu')
          return
        }
        if (multiplayerPhase === 'menu') {
          setMultiplayerPhase('idle')
          return
        }
        if (multiplayerPhase === 'lobby') {
          handleLobbyExit()
          return
        }
        if (multiplayerPhase === 'battle') {
          handleBattleExit(null)
          return
        }

        if (state.phase === 'title') {
          setTitleBackRequestId((id) => id + 1)
          return
        }

        returnToTitle()
      })

      if (cancelled) {
        await subscription.remove()
        subscription = null
      }
    })()

    return () => {
      cancelled = true
      if (subscription) void subscription.remove()
    }
  }, [
    error,
    handleBattleExit,
    handleLobbyExit,
    inviteLoginOpen,
    inviteOpen,
    isReady,
    multiplayerPhase,
    returnToTitle,
    state.phase,
  ])

  // --- 起動失敗時のエラー画面 ---
  // 再読み込み / LINE 再ログインを提供する。メイン UI はレンダリングされない。
  if (error) {
    const isNative = isCapacitorNative()
    const handleLineLogin = () => {
      try {
        ;(window as unknown as { liff?: { login?: () => void } }).liff?.login?.()
      } catch {
        // fallback:ok — LIFF ログインが失敗したら通常のリロードに倒す。
        window.location.reload()
      }
    }

    return (
      <div className="error-screen">
        <div className="error-screen__inner">
          <h1>{requiresReauth ? t('reauthRequiredTitle') : t('startupFailedTitle')}</h1>
          <p className="error-screen__message">{error}</p>
          <div className="error-screen__actions">
            {isNative ? (
              <>
                {requiresReauth ? (
                  <>
                    <button
                      className="btn btn--primary"
                      onClick={() => setReauthLoginOpen(true)}
                    >
                      {t('reauthButton')}
                    </button>
                    <button
                      className="btn btn--secondary"
                      onClick={() => window.location.reload()}
                    >
                      {t('reloadButton')}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn--primary"
                    onClick={() => window.location.reload()}
                  >
                    {t('reloadButton')}
                  </button>
                )}
              </>
            ) : (
              <>
                {requiresReauth ? (
                  <>
                    <button
                      className="btn btn--primary"
                      onClick={handleLineLogin}
                    >
                      {t('lineReloginButton')}
                    </button>
                    <button
                      className="btn btn--secondary"
                      onClick={() => window.location.reload()}
                    >
                      {t('reloadButton')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn--primary"
                      onClick={() => window.location.reload()}
                    >
                      {t('reloadButton')}
                    </button>
                    <button
                      className="btn btn--secondary"
                      onClick={handleLineLogin}
                    >
                      {t('lineReloginButton')}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {isNative && (
          <AuthLoginModal
            open={reauthLoginOpen}
            onClose={() => setReauthLoginOpen(false)}
            onLoggedIn={() => window.location.reload()}
            reason={t('reauthReason')}
            mode="recover"
          />
        )}
      </div>
    )
  }

  // --- 起動スプラッシュ ---
  // Phase 1-3 が完了するまではゲーム画面を描画しない。isReady=true で通常 UI に遷移する。
  if (!isReady) {
    return (
      <div className="splash">
        <img src={splashImage} alt="Loading..." className="splash__image" />
        <div className="splash__overlay">
          <div className="splash__progress">
            <p className="splash__message">{loadingMessage}</p>
            <div className="splash__bar">
              <div
                className="splash__bar-fill"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
            <p className="splash__percent">{Math.round(loadingProgress)}%</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="app-container">
      <OfflineBanner />
      {bannerAllowed && <AdBanner screen={bannerScreen} />}
      {state.phase === 'title' && multiplayerPhase !== 'lobby' && multiplayerPhase !== 'battle' && (
        <TitleScreen
          highscore={highscore}
          profile={profile}
          onStart={goToDifficulty}
          onRanking={goToRanking}
          onProfileUpdated={setProfile}
          onSettingsChange={() => setLocaleRevision((revision) => revision + 1)}
          onSoundEnabled={resumeBgmIfPaused}
          backRequestId={titleBackRequestId}
          onBackUnhandled={exitNativeApp}
        />
      )}
      {multiplayerPhase === 'lobby' && lobbyRoom && (
        <MultiplayerLobby
          room={lobbyRoom}
          currentUserId={currentUserId}
          onExit={handleLobbyExit}
          onBattleStart={currentUserId ? handleBattleStart : undefined}
        />
      )}
      {multiplayerPhase === 'battle' && lobbyRoom && currentUserId && profile && (
        <MultiplayerGameScreen
          room={lobbyRoom}
          selfUserId={currentUserId}
          selfProfile={profile}
          apiBaseUrl={YMG_API_BASE_URL}
          onExit={handleBattleExit}
        />
      )}
      {state.phase === 'difficulty' && (
        <DifficultyScreen
          initial={state.difficulty}
          onStart={handleStart}
          onBack={returnToTitle}
        />
      )}
      {showGame && (
        <>
          <GameScreen
            state={state}
            blackScore={blackScore}
            whiteScore={whiteScore}
            highscore={highscore}
            onCellClick={handleCellClick}
            onPopupComplete={handlePopupComplete}
            mode="npc"
            undoIsUnlocked={undoIsUnlocked}
            undoRemainingUses={undoRemainingUses}
            undoIsUnlocking={undoIsUnlocking}
            undoCanUndo={undoCanUndo}
            onUndoUnlock={handleUndoUnlock}
            onUndo={handleUndo}
          />
          {state.phase === 'intro' && (
            <IntroOverlay
              message={state.introMessage}
              playerColor={state.playerColor}
            />
          )}
          {state.phase === 'finale' &&
            state.finaleStep === 'announce' &&
            state.winner !== null &&
            state.finaleKind !== null && (
              <FinaleOverlay
                winnerColor={state.winner}
                playerColor={state.playerColor}
                kind={state.finaleKind}
              />
            )}
        </>
      )}
      {state.phase === 'result' && (
        <ResultScreen
          state={state}
          blackScore={blackScore}
          whiteScore={whiteScore}
          highscore={highscore}
          isNewHighscore={isNewHighscore}
          onRetry={handleRetry}
          onTitle={returnToTitle}
        />
      )}
      {state.phase === 'ranking' && (
        <RankingScreen onBack={returnToTitle} />
      )}

      {/* --- マルチプレイヤー系モーダル --- */}
      <MultiplayerMenu
        open={multiplayerPhase === 'menu'}
        onClose={() => setMultiplayerPhase('idle')}
        onRandomMatch={() => setMultiplayerPhase('random')}
        onFriendMatch={() => setMultiplayerPhase('friend')}
      />
      <MatchmakingModal
        open={multiplayerPhase === 'random'}
        onClose={() => setMultiplayerPhase('menu')}
        onMatched={handleMatched}
      />
      <FriendMatchModal
        open={multiplayerPhase === 'friend'}
        onClose={() => setMultiplayerPhase('menu')}
        onRoomReady={handleMatched}
      />

      {/* T16: フレンド招待モーダル */}
      <InviteFriendsModal
        open={inviteOpen}
        onClose={handleCloseInvite}
        onRequireLogin={handleInviteRequireLogin}
      />

      {/* 招待モーダル経由のログインダイアログ */}
      <AuthLoginModal
        open={inviteLoginOpen}
        onClose={() => setInviteLoginOpen(false)}
        onLoggedIn={handleInviteLoggedIn}
        reason={AUTH_UI.reasonOnlineMatch}
      />

      {/*
        リワード広告のモック・オーバーレイ。production 経路では表示されないが、
        起動段階で services/ads.ts に register しておく必要があるため常時 mount する。
      */}
      <AdNekoPopup />
      <AdRewardedOverlay />
    </div>
  )
}

/**
 * ユーザーに見せる起動エラーメッセージを返す。
 *
 * 内部の Error.message（ファイルパス・スタックフラグメント・サーバー返却メッセージ）を
 * そのまま画面に出すと、実装詳細の漏洩や UX の悪化につながる
 * （Red Team レビュー MEDIUM 指摘）。ここでエラー種別ごとに固定文言へマップする。
 *
 * 種別が判定できないケースは総称文言にフォールバックし、詳細は console に
 * 残す（ymgFetch / hubProfile で既に warn 済みなので呼び出し側で追加ログは不要）。
 */
function toUserFacingErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return t('errorReauth')
    if (err.status === 403) return t('errorForbidden')
    if (err.status === 404) return t('errorNotFound')
    if (err.status === 429) return t('errorRateLimited')
    if (err.status >= 500) return t('errorServer')
    return t('errorCommunication')
  }
  if (err instanceof NetworkError) {
    return t('errorNetwork')
  }
  return t('errorStartup')
}

function isReauthError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401 && err.requiresReauth === true
}

/**
 * スプラッシュ終了までにプリロードしておきたいゲーム内画像群。
 * 何らかの理由で 1 枚ロードに失敗してもゲームは壊れないので、全て Promise.all ではなく
 * allSettled 相当で扱い、失敗個別は無視する（プログレスは進める）。
 */
async function preloadSplashAssets(onProgress: (fraction: number) => void): Promise<void> {
  const assets = [bg2Image, block1Image, block2Image, titleLogoImage]
  let loaded = 0
  const total = assets.length

  await Promise.all(
    assets.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image()
          img.onload = () => {
            loaded += 1
            onProgress(loaded / total)
            resolve()
          }
          img.onerror = () => {
            // fallback:ok — 個別画像のロード失敗はゲームが壊れない。プログレスは進める。
            loaded += 1
            onProgress(loaded / total)
            resolve()
          }
          img.src = src
        }),
    ),
  )
}
